/* @flow */

import { call, put, take } from 'redux-saga/effects'

import { goToLibrary, goToOldLibrary, goToVerify } from '../stores/routing'
import { setIsLoggingIn } from '../stores/pending'
import { setLoginForm } from '../stores/loginForm'
import { intoRolesStore } from '../stores/rolesStore'
import { setCurrentUserId } from '../stores/currentUser'
import { setCurrentSettings } from '../stores/currentSettings'
import { setAlertSuccess, setAlertDanger } from '../stores/alert'
import {
  AUTH_TOKEN,
  OPINARY_USER,
  intoUsersStore,
  isToolUser,
} from '../stores/usersStore'
import {
  mapUserToCustomerStore,
  intoCustomersStore,
} from '../stores/customersStore'

import type { Action, LoginCredentials } from '../types'

import {
  hardcodedRoles,
  getUserFromLegacyResponse,
  getSettingsFromLegacyLoginResponse,
} from '../legacy'

import {
  BadResponseError,
  UnauthorizedError,
  localStorageSetItem,
  SagaMalfunctionError,
  sessionStorageSetItem,
} from '../helpers'

import { API_LOGIN_SCOPE_URL, API_LOGIN_URL } from '../urls'

import { sagaLoginPost } from '../fetch'
import { fetchInitialData } from './fetchInitialData'

export const LOGIN_BUTTON_CLICK: string = 'opinary/LOGIN_BUTTON_CLICK'

export function loginButtonClick(credentials: LoginCredentials): Action {
  return {
    type: LOGIN_BUTTON_CLICK,
    credentials,
  }
}

export function* sagaLogin(credentials: LoginCredentials): Generator<*, *, *> {
  const response = yield call(sagaLoginPost, API_LOGIN_URL, {
    ...credentials,
    scopes: [API_LOGIN_SCOPE_URL],
  })

  if (response == null) throw new SagaMalfunctionError()
  if (response.status === 401 || response.status === 403)
    throw new UnauthorizedError()
  if (response.status !== 200) throw new BadResponseError(response)

  return yield call([response, response.json])
}

export function* sagaLoginButtonClick(
  credentials: LoginCredentials
): Generator<*, void, *> {
  const json = yield call(sagaLogin, credentials)

  if (json == null) throw new SagaMalfunctionError()
  /*
   * On production authentication is done using the `auth` token supplied by the server as a cookie.
   * Cookie is appended to all requests automatically and works across subdomains.
   * On staging sessionStorage is used as a workaround needs to be used to store the token since domains are different.
   * Token is then appended to request headers in fetch/index. SessionStorage is used instead of localStorage
   * to 'forget' header token if it expires on a subdomain and give priority to cookie.
   */
  yield call(sessionStorageSetItem, AUTH_TOKEN, json.accessToken)
  yield call(localStorageSetItem, OPINARY_USER, json.account)
  const user = getUserFromLegacyResponse(json.account)
  yield put(intoRolesStore(hardcodedRoles))
  yield put(intoCustomersStore(mapUserToCustomerStore(user)))
  yield put(intoUsersStore([user]))
  yield put(setCurrentUserId(user.id))

  yield put(
    setCurrentSettings(getSettingsFromLegacyLoginResponse(json.account))
  )
  yield put(fetchInitialData())

  if (isToolUser(user)) {
    if (user.mfaEnabled) {
      goToVerify()
    }
    goToLibrary()
  } else {
    yield put(goToOldLibrary())
    yield put(
      setLoginForm({
        name: '',
        password: '',
      })
    )
    yield put(setAlertSuccess(`Welcome ${user.name}.`))
  }
}

export function* sagaRepeatedlyLoginButtonClick(): Generator<*, void, *> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const action = yield take(LOGIN_BUTTON_CLICK)
    if (action == null) throw new SagaMalfunctionError()

    yield put(setIsLoggingIn(true))
    try {
      yield call(sagaLoginButtonClick, action.credentials)
    } catch (err) {
      yield put(setAlertDanger('Incorrect username or password.'))
      yield put(
        setLoginForm({
          name: action.credentials.name,
          password: '',
        })
      )
    }
    yield put(setIsLoggingIn(false))
  }
}



// WEBPACK FOOTER //
// ./src/sagas/login.js